import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridToolbar } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { lightGreen, red } from '@mui/material/colors';
import { useLayout } from '../../../context/LayoutContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { toast } from 'react-toastify';

import {
  useGetShiftRequestsByShiftIdQuery,
} from '../../../slices/shiftRequestApiSlice';

import {
  useGetAgenciesQuery,
} from '../../../slices/agencyApiSlice';
import { useAssignShiftToAgencyMutation } from '../../../slices/shiftApiSlice';
import {
  useGetShiftsQuery,
  useAssignShiftToUserMutation,
} from '../../../slices/shiftApiSlice';
import { useGetApprovedFilledShiftsQuery } from '../../../slices/shiftApiSlice';

// ADD THIS IMPORT
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';

import { useUpdateShiftRequestStatusMutation } from '../../../slices/shiftRequestApiSlice';

// Styled DataGrid with conditional row styles
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.lightGreenRow {
    background-color: ${lightGreen[100]};
  }
  & .${gridClasses.row}.redRow {
    background-color: ${red[100]};
  }
     & .${gridClasses.row}.lightOrangeRow {
    background-color: #ffe0b2; /* Light orange */
  }
  & .${gridClasses.row}.greenRow {
    background-color: #c8e6c9; /* Soft green */
  }

  & .${gridClasses.row}.darkGreenRow {
    background-color:rgb(149, 240, 152); /* Dark green */
  }

`;

const ShiftWardListScreen = () => {
  const navigate = useNavigate();
  const [updateShiftRequestStatus] = useUpdateShiftRequestStatusMutation();

  console.log("ShiftWardListScreen....",)
  
  // ADD THIS: Fetch hospital locations
  const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery();
  
  const { data: shifts, isLoading, isError, error, refetch: refetchShifts } = useGetApprovedFilledShiftsQuery();

  const [openModal, setOpenModal] = React.useState(false);
  const [selectedShift, setSelectedShift] = React.useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [selectedFiller, setSelectedFiller] = useState(null); // staff or agency object
  const [isAgencyFilling, setIsAgencyFilling] = useState(false);
  const [selectedShiftRow, setSelectedShiftRow] = useState(null);
  const { userInfo } = useSelector((state) => state.auth);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [assignShiftToAgency] = useAssignShiftToAgencyMutation();
  const { setFluid } = useLayout();

  // ============= MANAGER DIRECTORATE FILTERING =============
  // Get the manager's directorate from userInfo
  const managerDirectorate = userInfo?.directorateName || null;
  
  // Check if user is a manager
  const isManager = userInfo?.isManager === true;
  const isAdmin = userInfo?.isAdmin === true;
  
  console.log('User Info:', { 
    isAdmin, 
    isManager, 
    directorate: managerDirectorate 
  });
  
  // Create a mapping of ward to directorate
  const wardToDirectorateMap = useMemo(() => {
    if (!hospitalLocations || hospitalLocations.length === 0) return {};
    
    const map = {};
    hospitalLocations.forEach(location => {
      if (location.wardDepartmentArea && location.directorate) {
        map[location.wardDepartmentArea] = location.directorate;
      }
    });
    return map;
  }, [hospitalLocations]);
  
  // Filter shifts based on manager's directorate
  const filteredShifts = useMemo(() => {
    if (!shifts) return [];
    
    // Admin sees all shifts
    if (isAdmin) return shifts;
    
    // Manager sees only shifts in their directorate
    if (isManager && managerDirectorate) {
      const filtered = shifts.filter(shift => {
        const shiftWard = shift.wardDepartmentArea;
        if (!shiftWard) return false;
        
        const wardDirectorate = wardToDirectorateMap[shiftWard];
        return wardDirectorate === managerDirectorate;
      });
      
      console.log(`Filtered shifts for directorate "${managerDirectorate}": ${filtered.length} of ${shifts.length}`);
      return filtered;
    }
    
    // Default: show all shifts
    return shifts;
  }, [shifts, isAdmin, isManager, managerDirectorate, wardToDirectorateMap]);
  
  // Get unique wards for display/info
  const uniqueWards = useMemo(() => {
    if (!filteredShifts) return [];
    return [...new Set(filteredShifts.map(shift => shift.wardDepartmentArea).filter(Boolean))];
  }, [filteredShifts]);
  // ============= END MANAGER DIRECTORATE FILTERING =============

  const handleConfirmAssignment = async () => {
    try {
      if (!selectedShift || !selectedFiller) {
        toast.error('Please select both a shift and a filler');
        return;
      }

      if (isAgencyFilling) {
        // Call the agency assignment function
        await handleAgencyApprove(selectedFiller);
        refetch(); // This triggers a refresh
      } else {
        // Construct a fake request object to match handleApprove signature
        const request = {
          requestedBy: null,
          requestedByName: "",
          requestedByEmail: "",
          lineStatus: "Open"
        };
        await handleApprove(request);
      }

      // Find the approved request related to this shift (if any)
      const approvedRequest = shiftRequests?.find(
        (req) => req.lineStatus === "Approved"
      );

      if (approvedRequest) {
        await updateShiftRequestStatus({
          shiftRequestId: approvedRequest._id,
          lineStatus: "Open",
        }).unwrap();
      }

      refetch(); // This triggers a refresh
      await refetchShifts(); // ✅ refresh the main list

      setOpenConfirmModal(false);
      setOpenModal(false); 

      setSelectedShift(null);
      setSelectedFiller(null);
      toast.success('Shift Reset!');
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to reset shift');
    }
  };

  const handleAssignClick = (shift, filler, isAgency = false) => {
    setSelectedShift(shift);
    setSelectedFiller(filler);
    setSelectedShiftId(shift._id);
    setIsAgencyFilling(isAgency);
    setOpenConfirmModal(true);
  };

  const {
    data: shiftRequests,
    refetch,
    isLoading: loadingRequests,
  } = useGetShiftRequestsByShiftIdQuery(selectedShiftId, {
    skip: !selectedShiftId,
  });

  const { data: agencies, isLoading: loadingAgencies } = useGetAgenciesQuery();

  const [assignShift] = useAssignShiftToUserMutation();

  const handleAgencyApprove = async (agency) => {
    try {
      await assignShiftToAgency({
        shiftId: selectedShiftId,
        agencyId: null,
        agencyName: "",
        agencyEmail: "",
        managerId: userInfo._id,
        lineStatus: 'Open',
      }).unwrap();
  
      refetch(); // To reload shift data
    } catch (err) {
      console.error('Failed to assign shift to agency:', err);
    }
  };
  
  useEffect(() => {
    // Enable full-width container
    setFluid(true);

    return () => {
      // Reset back to normal when screen unmounts
      setFluid(false);
    };
  }, [setFluid]);

  const handleApprove = async (request) => {
    try {
      await assignShift({
        shiftId: selectedShiftId,
        userId: null,
        userName: "",
        userEmail: "",
        managerId: userInfo._id,
        lineStatus: 'Open',
      }).unwrap();
  
      refetch();
    } catch (err) {
      console.error('Failed to assign shift:', err);
    }
  };

  const columns = [
    {
      field: 'startDateTime',
      headerName: 'Start Time',
      width: 180,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.startDateTime),
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'endDateTime',
      headerName: 'End Time',
      width: 180,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.endDateTime),
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    { field: 'wardDepartmentArea', headerName: 'Ward/Dept Area', width: 150 },
    { field: 'categoryOfStaff', headerName: 'Staff Category', width: 150 },
    { field: 'lineStatus', headerName: 'Line Status', width: 120 },
    { field: 'filledByName', headerName: 'Filled By', width: 120 },
    {
      field: 'specialRequirements',         
      headerName: 'Special Requirements',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/manager/shiftsWard/${params.row._id}/edit`)}
        >
          Approve
        </Button>
      ),
    },
  ];

  const getRowClassName = (params) => {
    const now = new Date();
    const end = new Date(params.row.endDateTime);

    if (end < now) return 'redRow';

    const status = params.row.lineStatus?.toLowerCase();

    switch (status) {
      case 'open':
      case 'on hold':
        return 'lightOrangeRow';
      case 'archived':
      case 'filledbystaff':
      case 'filled':
      case 'complete':
        return 'greenRow';
      case 'filledbyagency':
        return 'darkGreenRow';
      case 'cancelled':
        return 'redRow';
      default:
        return 'lightGreenRow';
    }
  };

  // Show loading state while fetching either shifts or locations
  if (isLoading || loadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4">Ward, Approve and update worked shifts</Typography>
          {isManager && managerDirectorate && (
            <Typography variant="subtitle1" color="primary" sx={{ mt: 1 }}>
              Viewing shifts for directorate: {managerDirectorate} ({uniqueWards.length} wards)
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
        >
          Create Shift
        </Button>
      </Box>

      <StyledDataGrid
        density="compact"
        rows={filteredShifts || []} // Use filteredShifts instead of shifts
        columns={columns}
        getRowId={(row) => row._id}
        getRowClassName={getRowClassName}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sortModel={[{ field: 'startDateTime', sort: 'desc' }]}
        disableSelectionOnClick
        onRowClick={(params) => {
          setSelectedShift(params.row);
          setOpenModal(true);
        }}
      />

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Shift Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedShift ? (
            <Box>
              <Typography variant="body1"><strong>Start:</strong> {new Date(selectedShift.startDateTime).toLocaleString()}</Typography>
              <Typography variant="body1"><strong>End:</strong> {new Date(selectedShift.endDateTime).toLocaleString()}</Typography>
              <Typography variant="body1"><strong>Ward/Dept:</strong> {selectedShift.wardDepartmentArea}</Typography>
              <Typography variant="body1"><strong>Category of Staff:</strong> {selectedShift.categoryOfStaff}</Typography>
              <Typography variant="body1"><strong>Line Status:</strong> {selectedShift.lineStatus}</Typography>
              <Typography variant="body1"><strong>Filled By:</strong> {selectedShift.filledByName || 'Unassigned'}</Typography>
              <Typography variant="body1"><strong>Special Requirements:</strong> {selectedShift.specialRequirements || 'None'}</Typography>
              
              {/* Show directorate info if available */}
              {selectedShift.wardDepartmentArea && wardToDirectorateMap[selectedShift.wardDepartmentArea] && (
                <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                  <strong>Directorate:</strong> {wardToDirectorateMap[selectedShift.wardDepartmentArea]}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>No shift selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmAssignment} 
            color="primary" 
            variant="contained"
          >
            Confirm Shift ReSet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftWardListScreen;